import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNotificationModal } from '../contexts/NotificationModalContext';
import { useUserDesignations } from '../contexts/UserDesignationsContext';
import { auth } from '../config/firebase';
import api from '../services/api';
import { ListCardSkeleton, UserListSkeleton } from '../components/Skeleton';
import { formatLocationDisplay, formatLocationOverride } from '../utils/locationUtils';
import ListHeroPhoto from '../components/ListHeroPhoto';
import AutoFollowModal from '../components/AutoFollowModal';
import CollaboratorsModal from '../components/CollaboratorsModal';
import '../styles/Discover.css';

// Debounce utility
function useDebounce(callback, delay) {
  const timeoutRef = useRef(null);

  const debouncedCallback = useCallback((...args) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}

// Format time since last update
const getTimeSince = (dateString) => {
  if (!dateString) return null;

  // Handle Firestore Timestamp objects
  let date;
  if (dateString && typeof dateString === 'object' && dateString._seconds) {
    // Firestore Timestamp
    date = new Date(dateString._seconds * 1000);
  } else if (dateString && typeof dateString === 'object' && dateString.seconds) {
    // Firestore Timestamp (alternative format)
    date = new Date(dateString.seconds * 1000);
  } else {
    date = new Date(dateString);
  }

  // Check for invalid date
  if (isNaN(date.getTime())) return null;

  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1d ago';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

// Separate caches for static and personalized content
// Static content (same for all users) can be cached longer - backend caches for 15 min
const STATIC_CACHE_TTL = 30 * 60 * 1000; // 30 minutes - static content rarely changes
const PERSONALIZED_CACHE_TTL = 5 * 60 * 1000; // 5 minutes - varies by user but still cacheable

let staticMemoryCache = { data: null, timestamp: 0 };
let personalizedMemoryCache = { data: null, timestamp: 0 };

const getStaticCache = () => {
  if (!staticMemoryCache.data) return null;
  const isStale = Date.now() - staticMemoryCache.timestamp > STATIC_CACHE_TTL;
  return { data: staticMemoryCache.data, timestamp: staticMemoryCache.timestamp, isStale };
};

const setStaticCache = (data) => {
  staticMemoryCache = { data, timestamp: Date.now() };
};

const getPersonalizedCache = () => {
  if (!personalizedMemoryCache.data) return null;
  const isStale = Date.now() - personalizedMemoryCache.timestamp > PERSONALIZED_CACHE_TTL;
  return { data: personalizedMemoryCache.data, timestamp: personalizedMemoryCache.timestamp, isStale };
};

const setPersonalizedCache = (data) => {
  personalizedMemoryCache = { data, timestamp: Date.now() };
};

// Memoized ListCard component to prevent unnecessary re-renders

const ListCard = memo(function ListCard({ list, percentVisited, visitedIdsLoaded, onListClick, onUserClick, onCollaboratorsClick, onTripClick }) {
  const items = list.items || [];
  const timeSince = getTimeSince(list.updatedAt || list.createdAt);
  const locationDisplay = formatLocationOverride(list.locationOverride)
    || formatLocationDisplay(list.location)
    || list.location;
  const categoryDisplay = list.categoryOverride || list.category;
  // Support both summaryOnly (itemCount) and full (items.length) responses
  const placeCount = list.itemCount ?? items.length;
  const placeLabel = placeCount === 1 ? 'place' : 'places';
  const followersCount = list.followersCount || 0;

  // Support both summaryOnly (heroItem) and full (search items) responses
  const heroItem = list.heroItem || items.find(item => item.photoUrl);
  const heroPlaceId = heroItem?.placeId;

  // Check if this is a collaborative list
  const collaboratorCount = (list.collaborators || []).length;
  const isCollaborative = collaboratorCount > 0;

  return (
    <div key={list.id} className="list-preview-card" onClick={() => onListClick(list.id)}>
      <div className="list-preview-hero">
        <ListHeroPhoto
          placeId={heroPlaceId}
          altText={list.title || list.name}
          storedPhotoUrl={heroItem?.photoUrl}
          photoUrlSavedAt={heroItem?.photoUrlSavedAt}
          customPhoto={list.customPhoto}
        />
        <div className="list-hero-overlay" />
        {list.isCreatorFeatured && (
          <div className="promoted-badge">
            Promoted
          </div>
        )}
        {isCollaborative && (
          <div className="collaborative-badge">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
            </svg>
          </div>
        )}
        {list.creatorPhotoURL ? (
          <img
            src={list.creatorPhotoURL}
            alt={list.creatorName}
            className="creator-avatar-badge"
            onClick={(e) => { e.stopPropagation(); if (list.username) onUserClick(list.username); }}
          />
        ) : (list.creatorName || list.username) && (
          <div
            className="creator-avatar-badge creator-avatar-placeholder"
            onClick={(e) => { e.stopPropagation(); if (list.username) onUserClick(list.username); }}
          >
            {(list.creatorName || list.username || 'U').charAt(0).toUpperCase()}
          </div>
        )}
        {list.hasItinerary && (
          <button
            className="trip-plan-badge"
            onClick={(e) => { e.stopPropagation(); onTripClick(list.id); }}
            aria-label="View trip plan"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
              <line x1="16" y1="2" x2="16" y2="6"/>
              <line x1="8" y1="2" x2="8" y2="6"/>
              <line x1="3" y1="10" x2="21" y2="10"/>
            </svg>
            <span>Trip</span>
          </button>
        )}
      </div>
      <div className="list-preview-content">
        <h3 className="list-preview-title">{list.title || list.name}</h3>
        <div className="list-preview-creator">
          {isCollaborative ? (
            <span
              className="creator-link collaborative-link"
              onClick={(e) => onCollaboratorsClick(e, list.id)}
            >
              by {list.creatorName || list.username || 'Unknown'}{collaboratorCount > 0 && ` & ${collaboratorCount} other${collaboratorCount === 1 ? '' : 's'}`}
            </span>
          ) : (
            <span>by {list.username ? (
              <span className="creator-link" onClick={(e) => { e.stopPropagation(); onUserClick(list.username); }}>{list.creatorName || list.username}</span>
            ) : (list.creatorName || 'Unknown')}</span>
          )}
          {timeSince && <span className="meta-bullet">•</span>}
          {timeSince && <span>{timeSince}</span>}
        </div>
        <div className="list-preview-subtitle">
          {locationDisplay && <span>{locationDisplay}</span>}
          {locationDisplay && categoryDisplay && <span className="meta-bullet">•</span>}
          {categoryDisplay && <span>{categoryDisplay}</span>}
        </div>
        <div className="list-preview-meta">
          <span>{placeCount} {placeLabel}</span>
          <span className="meta-bullet">•</span>
          <span>{visitedIdsLoaded ? `${percentVisited}%` : '—'} visited</span>
          <span className="meta-bullet">•</span>
          <span>{followersCount} {followersCount === 1 ? 'follower' : 'followers'}</span>
        </div>
      </div>
    </div>
  );
});

function Discover() {
  const { user } = useAuth();
  const { isPremium } = useSubscription();
  const { showAlert, showConfirm } = useNotificationModal();
  // Use pre-computed visitedPlaceIds from context instead of computing locally
  const { visitedPlaceIds, visitedIdsLoaded } = useUserDesignations();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  // Initialize state from separate caches (static vs personalized)
  const staticCache = getStaticCache();
  const personalizedCache = getPersonalizedCache();
  const [featuredLists, setFeaturedLists] = useState(staticCache?.data?.featured || []);
  const [cityGuides, setCityGuides] = useState(staticCache?.data?.cityGuides || []);
  const [bucketListBuilders, setBucketListBuilders] = useState(staticCache?.data?.bucketListBuilders || []);
  const [publicLists, setPublicLists] = useState(personalizedCache?.data?.public || []);
  const [friendsLists, setFriendsLists] = useState(personalizedCache?.data?.friends || []);
  const [listSearchResults, setListSearchResults] = useState([]);
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Track loading state per section for progressive loading (only when no cache)
  const [sectionsLoading, setSectionsLoading] = useState({
    featured: false,
    public: false,
    friends: false,
    cityGuides: false,
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProfileData, setUserProfileData] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDifferenceModal, setShowDifferenceModal] = useState(false);
  const [differenceModalReturnUser, setDifferenceModalReturnUser] = useState(null);

  // Auto-follow modal state
  const [showAutoFollowModal, setShowAutoFollowModal] = useState(false);
  const [pendingFollowUser, setPendingFollowUser] = useState(null);

  // Collaborators modal state
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
  const [collaboratorsModalListId, setCollaboratorsModalListId] = useState(null);

  // Track Firebase auth readiness
  const [firebaseReady, setFirebaseReady] = useState(!!auth.currentUser);

  // Listen for Firebase auth state to know when we can make authenticated requests
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setFirebaseReady(!!firebaseUser);
    });
    return () => unsubscribe();
  }, []);

  // Helper to get user ID from profile data (handles different field names)
  const getProfileUserId = () => {
    if (!userProfileData) return null;
    return userProfileData.userId || userProfileData.id || userProfileData.uid;
  };

  // Container ref for scrolling
  const containerRef = useRef(null);

  // Fetch static content (same for all users - loads fast from public cache)
  const fetchStaticContent = useCallback(async (forceRefresh = false) => {
    // Wait for Firebase auth to be ready before making authenticated requests
    if (!auth.currentUser) {
      return;
    }

    const cache = getStaticCache();

    // If we have fresh cache and not forcing refresh, skip
    if (cache && !cache.isStale && !forceRefresh) {
      console.log('[Discover] Static content: using fresh cache');
      return;
    }

    // Show loading skeletons only if no cached data to display
    if (!cache?.data) {
      setSectionsLoading(prev => ({ ...prev, featured: true, cityGuides: true }));
    }

    try {
      const startTime = performance.now();
      const result = await api.getDiscoverStatic(10);
      const data = result.data || {};
      console.log(`[Discover] Static content fetched in ${Math.round(performance.now() - startTime)}ms (cached: ${result.cached})`);

      // Update static sections immediately
      setFeaturedLists(data.featured || []);
      setCityGuides(data.cityGuides || []);
      setBucketListBuilders(data.bucketListBuilders || []);

      // Cache static data
      setStaticCache({
        featured: data.featured || [],
        cityGuides: data.cityGuides || [],
        bucketListBuilders: data.bucketListBuilders || [],
      });

    } catch (error) {
      console.error('Error fetching static discover data:', error);
    } finally {
      setSectionsLoading(prev => ({ ...prev, featured: false, cityGuides: false }));
    }
  }, []);

  // Fetch personalized content (varies by user - may take longer)
  const fetchPersonalizedContent = useCallback(async (forceRefresh = false) => {
    if (!user) return;
    // Wait for Firebase auth to be ready before making authenticated requests
    if (!auth.currentUser) {
      return;
    }

    const cache = getPersonalizedCache();
    const userId = user.userId || user.id;

    // If we have fresh cache and not forcing refresh, skip
    if (cache && !cache.isStale && !forceRefresh) {
      console.log('[Discover] Personalized content: using fresh cache');
      return;
    }

    // Show loading skeletons only if no cached data to display
    if (!cache?.data) {
      setSectionsLoading(prev => ({ ...prev, public: true, friends: true }));
    }

    try {
      const startTime = performance.now();
      const result = await api.getDiscoverPersonalized(user.username, userId, 10);
      const data = result.data || {};
      console.log(`[Discover] Personalized content fetched in ${Math.round(performance.now() - startTime)}ms`);

      // Filter out current user's lists
      const filteredPublic = (data.public || []).filter(
        list => list.userId !== userId
      );
      const filteredFriends = (data.friends || []).filter(
        list => list.userId !== userId
      );

      // Update personalized sections
      setPublicLists(filteredPublic);
      setFriendsLists(filteredFriends);

      // Cache personalized data
      setPersonalizedCache({
        public: filteredPublic,
        friends: filteredFriends,
      });

    } catch (error) {
      console.error('Error fetching personalized discover data:', error);
    } finally {
      setSectionsLoading(prev => ({ ...prev, public: false, friends: false }));
    }
  }, [user]);

  // Fetch all discover data (static first for fast initial render, then personalized)
  const fetchDiscoverData = useCallback(async (forceRefresh = false) => {
    if (!user) return;

    setRefreshing(true);

    // Fetch both in parallel, but static content will likely return first
    // since it uses aggressive public caching
    await Promise.all([
      fetchStaticContent(forceRefresh),
      fetchPersonalizedContent(forceRefresh),
    ]);

    setRefreshing(false);
  }, [user, fetchStaticContent, fetchPersonalizedContent]);

  // Stale-while-revalidate: Show cached data immediately, fetch fresh in background
  useEffect(() => {
    if (!user || !firebaseReady) return;

    const sCache = getStaticCache();
    const pCache = getPersonalizedCache();

    // Check if either cache needs refresh
    const needsStaticRefresh = !sCache?.data || sCache.isStale;
    const needsPersonalizedRefresh = !pCache?.data || pCache.isStale;

    // If both caches exist but are stale, revalidate in background
    if (sCache?.data && pCache?.data && (sCache.isStale || pCache.isStale)) {
      // Only refresh stale caches
      if (needsStaticRefresh) fetchStaticContent(true);
      if (needsPersonalizedRefresh) fetchPersonalizedContent(true);
    }
    // If no cache at all, fetch immediately
    else if (!sCache?.data || !pCache?.data) {
      // Fetch missing data - static content will arrive first
      if (needsStaticRefresh) fetchStaticContent(false);
      if (needsPersonalizedRefresh) fetchPersonalizedContent(false);
    }
  }, [user, firebaseReady, fetchStaticContent, fetchPersonalizedContent]);

  // Web refresh handler
  const handleRefreshClick = () => {
    fetchDiscoverData(true); // Force refresh both
  };

  // Ref to track current search and cancel stale requests
  const searchAbortRef = useRef(null);

  // Handle combined search for lists and accounts
  const handleCombinedSearch = useCallback(async (query) => {
    // Cancel any pending search
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }

    if (!query.trim()) {
      setListSearchResults([]);
      setUserSearchResults([]);
      setLoading(false);
      return;
    }

    // Create new abort controller for this search
    const abortController = new AbortController();
    searchAbortRef.current = abortController;

    try {
      setLoading(true);
      // Search both lists and users in parallel
      const [listsResponse, usersResponse] = await Promise.all([
        api.searchLists(query),
        api.searchUsers(query)
      ]);

      // Only update state if this request wasn't aborted
      if (!abortController.signal.aborted) {
        setListSearchResults(listsResponse.data || []);
        setUserSearchResults(usersResponse.data || []);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error searching:', error);
        setListSearchResults([]);
        setUserSearchResults([]);
      }
    } finally {
      if (!abortController.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  // Debounced search - waits 300ms after user stops typing
  const debouncedSearch = useDebounce(handleCombinedSearch, 300);

  // Handle search input
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Show loading immediately for better UX, but debounce the actual search
    if (query.trim()) {
      setLoading(true);
    }
    debouncedSearch(query);
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setListSearchResults([]);
    setUserSearchResults([]);
    setLoading(false);
    // Cancel any pending search
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
  };

  // Cleanup search abort controller on unmount
  useEffect(() => {
    return () => {
      if (searchAbortRef.current) {
        searchAbortRef.current.abort();
      }
    };
  }, []);

  // Handle list click
  const handleListClick = useCallback((listId) => {
    navigate(`/list/${listId}`);
  }, [navigate]);

  // Handle trip plan click
  const handleTripClick = useCallback((listId) => {
    navigate(`/list/${listId}/trip`);
  }, [navigate]);

  // Handle user click to view profile
  const handleUserClick = (username) => {
    navigate(`/user/${username}`);
  };

  // Handle add friend
  const handleAddFriend = async (userId, username) => {
    setActionLoading(true);
    try {
      await api.sendFriendRequest(userId);
      // Refresh user profile data to show updated state
      const response = await api.getUserByUsername(username);
      setUserProfileData(response.data);
    } catch (error) {
      console.error('Error sending friend request:', error);
      showAlert(error.message || 'Failed to send friend request. Please try again.', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle accept incoming friend request
  const handleAcceptFriendRequest = async (userId, username) => {
    setActionLoading(true);
    try {
      // First get the request ID
      const requestResponse = await api.getIncomingRequestFromUser(userId);
      const requestId = requestResponse.data.id;
      // Accept the request
      await api.respondToFriendRequest(requestId, true);
      // Refresh user profile data
      const response = await api.getUserByUsername(username);
      setUserProfileData(response.data);
    } catch (error) {
      console.error('Error accepting friend request:', error);
      showAlert('Failed to accept friend request. Please try again.', { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle follow user - show auto-follow modal first
  const handleFollowUser = async (userId, username) => {
    // Store pending follow info and show modal
    setPendingFollowUser({ userId, username });
    setShowAutoFollowModal(true);
  };

  // Execute the actual follow after modal confirmation
  const executeFollowUser = async (autoFollowOption = 'none') => {
    if (!pendingFollowUser) return;
    const { userId, username } = pendingFollowUser;

    try {
      await api.followUser(userId, { autoFollowLists: autoFollowOption });

      // If 'all' or 'current' was selected, follow all current public lists
      if (autoFollowOption === 'all' || autoFollowOption === 'current') {
        try {
          const listsResponse = await api.getLists(userId);
          const userLists = listsResponse.data || listsResponse || [];
          const publicLists = userLists.filter(l => l.visibility === 'public');

          for (const list of publicLists) {
            try {
              await api.followList(list.id);
            } catch (e) {
              // Ignore if already following or other errors
            }
          }
        } catch (e) {
          // Error fetching lists to auto-follow - continue silently
        }
      }

      // Refresh user profile data
      const response = await api.getUserByUsername(username);
      setUserProfileData(response.data);
    } catch (error) {
      console.error('Error following user:', error);
      showAlert('Failed to follow user. Please try again.', { variant: 'error' });
    } finally {
      setPendingFollowUser(null);
    }
  };

  // Handle auto-follow modal confirmation
  const handleAutoFollowConfirm = (autoFollowOption) => {
    executeFollowUser(autoFollowOption);
  };

  // Handle unfollow user
  const handleUnfollowUser = async (userId, username) => {
    try {
      await api.unfollowUser(userId);
      // Refresh user profile data
      const response = await api.getUserByUsername(username);
      setUserProfileData(response.data);
    } catch (error) {
      console.error('Error unfollowing user:', error);
      showAlert('Failed to unfollow user. Please try again.', { variant: 'error' });
    }
  };

  // Handle unfriend user
  const handleUnfriend = async (userId, username) => {
    const confirmed = await showConfirm(`Remove @${username} as a friend?`, {
      title: 'Remove Friend',
      confirmText: 'Remove',
      variant: 'warning'
    });
    if (!confirmed) {
      return;
    }
    try {
      await api.unfriend(userId);
      // Refresh user profile data
      const response = await api.getUserByUsername(username);
      setUserProfileData(response.data);
    } catch (error) {
      console.error('Error removing friend:', error);
      showAlert('Failed to remove friend. Please try again.', { variant: 'error' });
    }
  };

  // Pre-compute percent visited for all displayed lists to avoid O(n) computation per card
  // Uses visitedPlaceIds from context (pre-computed once when designations change)
  // Supports both full items array (search results) and summaryOnly placeIds array (discover)
  const percentVisitedMap = useMemo(() => {
    const allLists = [...featuredLists, ...bucketListBuilders, ...publicLists, ...friendsLists, ...listSearchResults];
    const map = new Map();
    allLists.forEach(list => {
      // Support both summaryOnly (placeIds array) and full (items array) responses
      const placeIdList = list.placeIds || (list.items || []).map(item => item.placeId).filter(Boolean);
      if (placeIdList.length === 0) {
        map.set(list.id, 0);
      } else {
        const visitedCount = placeIdList.filter(placeId => visitedPlaceIds.has(placeId)).length;
        map.set(list.id, Math.round((visitedCount / placeIdList.length) * 100));
      }
    });
    return map;
  }, [featuredLists, bucketListBuilders, publicLists, friendsLists, listSearchResults, visitedPlaceIds]);

  // Calculate % visited for current user (not list owner) - uses pre-computed map
  const getPercentVisited = useCallback((list) => {
    return percentVisitedMap.get(list.id) ?? 0;
  }, [percentVisitedMap]);

  // Handle city guide click
  const handleCityGuideClick = (cityGuide) => {
    if (cityGuide.listCount > 0) {
      navigate(`/discover/city/${cityGuide.id}`);
    }
  };

  // Render city guide card
  const renderCityGuideCard = (cityGuide) => {
    const hasLists = cityGuide.listCount > 0;

    return (
      <div
        key={cityGuide.id}
        className={`city-guide-card ${!hasLists ? 'coming-soon' : ''}`}
        onClick={() => handleCityGuideClick(cityGuide)}
        style={{ cursor: hasLists ? 'pointer' : 'default' }}
      >
        <div className="city-guide-image">
          {cityGuide.imageUrl ? (
            <img src={cityGuide.imageUrl} alt={cityGuide.name} />
          ) : (
            <div className="city-guide-placeholder" />
          )}
          <div className="city-guide-overlay" />
        </div>
        <div className="city-guide-content">
          <h3 className="city-guide-name">{cityGuide.name}</h3>
          {!hasLists && <span className="city-guide-coming-soon">Coming Soon</span>}
          {hasLists && (
            <span className="city-guide-list-count">
              {cityGuide.listCount} {cityGuide.listCount === 1 ? 'list' : 'lists'}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Handle opening collaborators modal
  const handleCollaboratorsClick = useCallback((e, listId) => {
    e.stopPropagation();
    setCollaboratorsModalListId(listId);
    setShowCollaboratorsModal(true);
  }, []);

  // Stable callback for navigating to user profile
  const handleUserNavigation = useCallback((username) => {
    navigate(`/user/${username}`);
  }, [navigate]);

  // Render list card using memoized component
  const renderListCard = useCallback((list) => {
    return (
      <ListCard
        key={list.id}
        list={list}
        percentVisited={getPercentVisited(list)}
        visitedIdsLoaded={visitedIdsLoaded}
        onListClick={handleListClick}
        onUserClick={handleUserNavigation}
        onCollaboratorsClick={handleCollaboratorsClick}
        onTripClick={handleTripClick}
      />
    );
  }, [getPercentVisited, visitedIdsLoaded, handleListClick, handleUserNavigation, handleCollaboratorsClick, handleTripClick]);

  return (
    <div
      className="discover-container"
      ref={containerRef}
    >
      <div className="discover-content-wrapper">
        <div className="discover-search-row">
          <div className="discover-search-bar">
            <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input
              type="text"
              placeholder="Search lists and accounts..."
              value={searchQuery}
              onChange={handleSearchChange}
              aria-label="Search lists and accounts"
            />
            {searchQuery && (
              <button
                className="search-clear-btn"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>
          <button
            className="refresh-button"
            onClick={handleRefreshClick}
            disabled={refreshing}
            aria-label="Refresh"
          >
            <svg className={refreshing ? 'spinning' : ''} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
            </svg>
          </button>
        </div>

        <div className="discover-content">
        {searchQuery.trim() ? (
          // Combined search results
          <div className="search-results-section">
            {loading ? (
              <div className="search-loading-skeleton">
                <div className="search-results-group">
                  <h2 className="search-results-heading">Lists</h2>
                  <div className="lists-grid">
                    <ListCardSkeleton />
                    <ListCardSkeleton />
                  </div>
                </div>
                <div className="search-results-divider" />
                <div className="search-results-group">
                  <h2 className="search-results-heading">Accounts</h2>
                  <UserListSkeleton count={3} />
                </div>
              </div>
            ) : (
              <>
                {/* Lists results */}
                {listSearchResults.length > 0 && (
                  <div className="search-results-group">
                    <h2 className="search-results-heading">Lists</h2>
                    <div className="lists-grid">
                      {listSearchResults.map(renderListCard)}
                    </div>
                  </div>
                )}

                {/* Visual separator */}
                {listSearchResults.length > 0 && userSearchResults.length > 0 && (
                  <div className="search-results-divider" />
                )}

                {/* Users results */}
                {userSearchResults.length > 0 && (
                  <div className="search-results-group">
                    <h2 className="search-results-heading">Accounts</h2>
                    <div className="lists-grid">
                      {userSearchResults.map((foundUser) => (
                        <div
                          key={foundUser.username}
                          className="user-preview-card"
                          onClick={() => handleUserClick(foundUser.username)}
                        >
                          <div className={`user-preview-avatar ${foundUser.isPremium || foundUser.subscriptionTier === 'premium' ? 'premium-avatar' : ''}`}>
                            {(foundUser.profilePicture || foundUser.photoURL) ? (
                              <img src={foundUser.profilePicture || foundUser.photoURL} alt={foundUser.displayName || foundUser.username} />
                            ) : (
                              (foundUser.displayName || foundUser.name || foundUser.username || 'U').charAt(0).toUpperCase()
                            )}
                          </div>
                          <div className="user-preview-content">
                            <h3 className="user-preview-name">{foundUser.displayName || foundUser.name || foundUser.username}</h3>
                            <div className="user-preview-username">@{foundUser.username}</div>
                            <div className="user-preview-meta">
                              <span>{foundUser.listsCount || 0} {(foundUser.listsCount || 0) === 1 ? 'list' : 'lists'}</span>
                              <span className="meta-bullet">•</span>
                              <span>{foundUser.followersCount || 0} {(foundUser.followersCount || 0) === 1 ? 'follower' : 'followers'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No results */}
                {listSearchResults.length === 0 && userSearchResults.length === 0 && (
                  <div className="empty-state">No results found for "{searchQuery}"</div>
                )}
              </>
            )}
          </div>
        ) : (
          // Discovery feed with progressive loading per section
          <div className="lists-sections">
            {refreshing && <div className="refreshing-indicator">Refreshing...</div>}

            {/* Featured Lists - show skeleton while loading, content when ready */}
            {(sectionsLoading.featured || featuredLists.length > 0) && (
              <div className="lists-section">
                <h2>Featured Lists</h2>
                {sectionsLoading.featured ? (
                  <div className="horizontal-scroll-container">
                    <ListCardSkeleton />
                    <ListCardSkeleton />
                  </div>
                ) : (
                  <div className="horizontal-scroll-container">
                    {featuredLists.map(renderListCard)}
                  </div>
                )}
              </div>
            )}

            {/* City Guides - show skeleton while loading, content when ready */}
            {(sectionsLoading.cityGuides || cityGuides.length > 0) && (
              <div className="lists-section">
                <h2>City Guides</h2>
                {sectionsLoading.cityGuides ? (
                  <div className="horizontal-scroll-container">
                    <ListCardSkeleton />
                    <ListCardSkeleton />
                  </div>
                ) : (
                  <div className="horizontal-scroll-container">
                    {cityGuides.map(renderCityGuideCard)}
                  </div>
                )}
              </div>
            )}

            {/* Bucket List Builders - only show if populated */}
            {bucketListBuilders.length > 0 && (
              <div className="lists-section">
                <h2>Bucket List Builders</h2>
                <div className="horizontal-scroll-container">
                  {bucketListBuilders.map(renderListCard)}
                </div>
              </div>
            )}

            {/* Public Lists - only show if populated */}
            {(sectionsLoading.public || publicLists.length > 0) && (
              <div className="lists-section">
                <h2>Public Lists</h2>
                {sectionsLoading.public ? (
                  <div className="horizontal-scroll-container">
                    <ListCardSkeleton />
                    <ListCardSkeleton />
                  </div>
                ) : (
                  <div className="horizontal-scroll-container">
                    {publicLists.map(renderListCard)}
                  </div>
                )}
              </div>
            )}

            {/* Friends Lists - only show if populated */}
            {(sectionsLoading.friends || friendsLists.length > 0) && (
              <div className="lists-section">
                <h2>Your Friends' Lists</h2>
                {sectionsLoading.friends ? (
                  <div className="horizontal-scroll-container">
                    <ListCardSkeleton />
                    <ListCardSkeleton />
                  </div>
                ) : (
                  <div className="horizontal-scroll-container">
                    {friendsLists.map(renderListCard)}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        </div>
      </div>

      {/* User Profile Modal */}
      {selectedUser && userProfileData && (
        <>
          <div className="modal-overlay" onClick={() => setSelectedUser(null)} aria-hidden="true" />
          <div className="user-profile-modal" role="dialog" aria-modal="true" aria-labelledby="user-profile-modal-title">
            <div className="modal-header">
              <h2 id="user-profile-modal-title">Profile</h2>
              <button
                className="modal-close-btn"
                onClick={() => setSelectedUser(null)}
                aria-label="Close profile"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-content">
              <div className="profile-section-modal">
                <div className={`profile-avatar-large ${userProfileData.isPremium || userProfileData.subscriptionTier === 'premium' ? 'premium-avatar' : ''}`}>
                  {(userProfileData.profilePicture || userProfileData.photoURL) ? (
                    <img src={userProfileData.profilePicture || userProfileData.photoURL} alt={userProfileData.displayName || userProfileData.username} />
                  ) : (
                    <span className="profile-initial-large">
                      {(userProfileData.displayName || userProfileData.name || userProfileData.username || 'U').charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <h3 className="profile-name">{userProfileData.displayName || userProfileData.name || userProfileData.username}</h3>
                <div className="profile-username">@{userProfileData.username}</div>
                {userProfileData.bio && <p className="profile-bio">{userProfileData.bio}</p>}
              </div>

              <div className="profile-stats">
                <div className="stat stat-non-clickable">
                  <div className="stat-value">{userProfileData.listsCount || 0}</div>
                  <div className="stat-label">Lists</div>
                </div>
                <div className="stat stat-non-clickable">
                  <div className="stat-value">{userProfileData.followersCount || 0}</div>
                  <div className="stat-label">Followers</div>
                </div>
                <div className="stat stat-non-clickable">
                  <div className="stat-value">{userProfileData.followingCount || 0}</div>
                  <div className="stat-label">Following</div>
                </div>
              </div>

              <div className="profile-actions">
                {userProfileData.areFriends ? (
                  <button
                    className="btn-friends"
                    onClick={() => handleUnfriend(getProfileUserId(), userProfileData.username)}
                    disabled={actionLoading}
                  >
                    Remove Friend
                  </button>
                ) : userProfileData.hasIncomingFriendRequest ? (
                  <button
                    className="btn-accept-request"
                    onClick={() => handleAcceptFriendRequest(getProfileUserId(), userProfileData.username)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? '...' : 'Accept Friend Request'}
                  </button>
                ) : userProfileData.hasPendingFriendRequest ? (
                  <button className="btn-pending" disabled>
                    Request Pending
                  </button>
                ) : (
                  <button
                    className="btn-add-friend"
                    onClick={() => handleAddFriend(getProfileUserId(), userProfileData.username)}
                    disabled={actionLoading}
                  >
                    {actionLoading ? '...' : 'Add as Friend'}
                  </button>
                )}
                {userProfileData.isFollowing ? (
                  <button
                    className="btn-unfollow"
                    onClick={() => handleUnfollowUser(getProfileUserId(), userProfileData.username)}
                    disabled={actionLoading}
                  >
                    Unfollow
                  </button>
                ) : (
                  <button
                    className="btn-follow"
                    onClick={() => handleFollowUser(getProfileUserId(), userProfileData.username)}
                    disabled={actionLoading}
                  >
                    Follow
                  </button>
                )}
              </div>
              <button
                className="btn-difference-link"
                onClick={() => { setDifferenceModalReturnUser(selectedUser); setSelectedUser(null); setShowDifferenceModal(true); }}
              >
                What's the difference?
              </button>
            </div>
          </div>
        </>
      )}

      {/* What's the Difference Modal */}
      {showDifferenceModal && (
        <>
          <div className="modal-overlay" onClick={() => { setShowDifferenceModal(false); setDifferenceModalReturnUser(null); }} aria-hidden="true" />
          <div className="difference-modal" role="dialog" aria-modal="true" aria-labelledby="difference-modal-title">
            <div className="difference-header">
              <button
                className="difference-back-btn"
                onClick={() => { setShowDifferenceModal(false); if (differenceModalReturnUser) { setSelectedUser(differenceModalReturnUser); } setDifferenceModalReturnUser(null); }}
                aria-label="Go back"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <h2 id="difference-modal-title" className="sr-only">Friends vs Following</h2>
              <div className="header-spacer"></div>
              <button
                className="difference-close-btn"
                onClick={() => { setShowDifferenceModal(false); setDifferenceModalReturnUser(null); }}
                aria-label="Close"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-content">
              <div className="difference-section">
                <h3>Friends</h3>
                <ul>
                  <li>Requires mutual approval - they must accept your request</li>
                  <li>See each other's friends-only lists</li>
                  <li>Share lists with specific friends</li>
                  <li>Appears in your Friends list on the Following page</li>
                </ul>
              </div>
              <div className="difference-section">
                <h3>Following</h3>
                <ul>
                  <li>One-way, no approval needed</li>
                  <li>Get notified when they create new public lists</li>
                  <li>Appears in your Accounts list on the Following page</li>
                  <li>Great for discovering new content</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Auto-Follow Modal */}
      <AutoFollowModal
        isOpen={showAutoFollowModal}
        onClose={() => {
          setShowAutoFollowModal(false);
          setPendingFollowUser(null);
        }}
        onConfirm={handleAutoFollowConfirm}
        targetUser={userProfileData}
        actionType="follow"
      />

      {/* Collaborators Modal */}
      <CollaboratorsModal
        isOpen={showCollaboratorsModal}
        onClose={() => {
          setShowCollaboratorsModal(false);
          setCollaboratorsModalListId(null);
        }}
        listId={collaboratorsModalListId}
      />
    </div>
  );
}

export default Discover;
